import { useSyncExternalStore } from 'react'

export type RecordStatus = 'active' | 'archived'

export type DepartmentRecord = {
  id: string
  name: string
  status: RecordStatus
}

export type DepartmentBudgetAllocations = Record<string, Record<string, number>>

export const USER_ROLES = [
  'Admin',
  'Company Director',
  'Project Director',
  'Project Manager',
  'Engineer',
] as const

export type UserRole = (typeof USER_ROLES)[number]

export type AppUser = {
  id: string
  firstName: string
  lastName: string
  email: string
  role: UserRole
  hourlyCost: number
  profileUrl?: string
  status: RecordStatus
}

type SettingsState = {
  departments: DepartmentRecord[]
  departmentBudgetAllocations: DepartmentBudgetAllocations
  users: AppUser[]
}

function createInitialDepartmentBudgetAllocations(): DepartmentBudgetAllocations {
  const now = new Date()
  const monthKeys = [0, 1, 2].map((offset) => {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  })

  return {
    'department-survey': {
      [monthKeys[0]]: 24000,
      [monthKeys[1]]: 28000,
      [monthKeys[2]]: 26000,
    },
    'department-design': {
      [monthKeys[0]]: 32000,
      [monthKeys[1]]: 36000,
      [monthKeys[2]]: 34000,
    },
    'department-delivery': {
      [monthKeys[0]]: 48000,
      [monthKeys[1]]: 52000,
      [monthKeys[2]]: 50000,
    },
    'department-finance': {
      [monthKeys[0]]: 12000,
      [monthKeys[1]]: 12000,
      [monthKeys[2]]: 12000,
    },
  }
}

const initialState: SettingsState = {
  departments: [
    { id: 'department-survey', name: 'Surveying', status: 'active' },
    { id: 'department-design', name: 'Design', status: 'active' },
    { id: 'department-delivery', name: 'Project Delivery', status: 'active' },
    { id: 'department-finance', name: 'Finance', status: 'active' },
    { id: 'department-legacy', name: 'Legacy Operations', status: 'archived' },
  ],
  departmentBudgetAllocations: createInitialDepartmentBudgetAllocations(),
  users: [
    {
      id: 'user-matthew',
      firstName: 'Matthew',
      lastName: 'Carter',
      email: 'matthew@gca-consulting.com',
      role: 'Company Director',
      hourlyCost: 95,
      status: 'active',
    },
    {
      id: 'user-sarah',
      firstName: 'Sarah',
      lastName: 'Mitchell',
      email: 'sarah.mitchell@gca-consulting.com',
      role: 'Project Director',
      hourlyCost: 78,
      status: 'active',
    },
    {
      id: 'user-priya',
      firstName: 'Priya',
      lastName: 'Patel',
      email: 'priya.patel@gca-consulting.com',
      role: 'Project Manager',
      hourlyCost: 65,
      status: 'active',
    },
    {
      id: 'user-david',
      firstName: 'David',
      lastName: 'Reyes',
      email: 'david.reyes@gca-consulting.com',
      role: 'Engineer',
      hourlyCost: 52,
      status: 'archived',
    },
  ],
}

let state = initialState
const listeners = new Set<() => void>()

function updateState(updater: (current: SettingsState) => SettingsState) {
  state = updater(state)
  listeners.forEach((listener) => listener())
}

function createId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

export function useSettingsStore() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => state,
    () => state,
  )
}

export const settingsActions = {
  addDepartment(name: string) {
    updateState((current) => ({
      ...current,
      departments: [
        ...current.departments,
        { id: createId('department'), name, status: 'active' },
      ],
    }))
  },
  toggleDepartmentStatus(id: string) {
    updateState((current) => ({
      ...current,
      departments: current.departments.map((department) =>
        department.id === id
          ? {
              ...department,
              status: department.status === 'active' ? 'archived' : 'active',
            }
          : department,
      ),
    }))
  },
  updateDepartmentBudget(
    departmentId: string,
    monthKey: string,
    amount: number,
  ) {
    updateState((current) => ({
      ...current,
      departmentBudgetAllocations: {
        ...current.departmentBudgetAllocations,
        [departmentId]: {
          ...(current.departmentBudgetAllocations[departmentId] || {}),
          [monthKey]: amount,
        },
      },
    }))
  },
  addUser(user: Omit<AppUser, 'id' | 'status'>) {
    updateState((current) => ({
      ...current,
      users: [
        ...current.users,
        { id: createId('user'), status: 'active', ...user },
      ],
    }))
  },
  updateUser(id: string, patch: Partial<Omit<AppUser, 'id'>>) {
    updateState((current) => ({
      ...current,
      users: current.users.map((user) =>
        user.id === id ? { ...user, ...patch } : user,
      ),
    }))
  },
  toggleUserStatus(id: string) {
    updateState((current) => ({
      ...current,
      users: current.users.map((user) =>
        user.id === id
          ? {
              ...user,
              status: user.status === 'active' ? 'archived' : 'active',
            }
          : user,
      ),
    }))
  },
}
